import { useState, useEffect, useCallback } from 'react';
import { githubService } from '../services/githubService';

interface UseResumeLoaderResult {
  resumeContent: string | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refreshResume: () => Promise<void>;
  cacheInfo: {
    hasCachedContent: boolean;
    isValid: boolean;
    timestamp?: number;
    age?: number | null;
  };
}

export const useResumeLoader = (autoRefreshInterval?: number): UseResumeLoaderResult => {
  const [resumeContent, setResumeContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadResume = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const content = forceRefresh 
        ? await githubService.refreshCache()
        : await githubService.fetchResumeContent();
      
      setResumeContent(content);
      setLastUpdated(new Date());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load resume';
      console.error('Resume loading error:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshResume = useCallback(async () => {
    await loadResume(true);
  }, [loadResume]);

  // Initial load
  useEffect(() => {
    loadResume();
  }, [loadResume]);

  // Auto-refresh interval (if specified)
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;

    const interval = setInterval(() => {
      const cacheInfo = githubService.getCacheInfo();
      if (!cacheInfo.isValid) {
        console.log('Cache expired, refreshing resume content...');
        loadResume();
      }
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshInterval, loadResume]);

  // Get cache info
  const cacheInfo = githubService.getCacheInfo();

  return {
    resumeContent,
    isLoading,
    error,
    lastUpdated,
    refreshResume,
    cacheInfo
  };
};